import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Slide, toast, ToastContainer } from 'react-toastify';

import { useInactiveBrowserTab } from 'hooks/useInactiveBrowserTab';
import { useMedia } from 'hooks/useMedia';
import { getAuthenticated } from 'store/auth/selectors';

import {
  MessageToast,
  MessageToastContent,
  MessageToastProps,
  SystemToast,
  SystemToastProps,
  SystemToastTypes,
} from 'components/shared/Toast';

import {
  DEFAULT_TOAST_AUTOCLOSE_TIME,
  MAX_TOASTS_COUNT_AT_TIME,
} from './constants';

import styles from './styles.module.sass';

const getMessageToastContent = (options: MessageToastContent) => {
  return <MessageToast {...options} />;
};

const getSystemToastContent = (options: SystemToastProps) => {
  return <SystemToast {...options} />;
};

export const ToastsContext = createContext<{
  showMessageToast: (payload: MessageToastProps) => void;
  showSuccessToast: (payload: Omit<SystemToastProps, 'type'>) => void;
  showWarningToast: (payload: Omit<SystemToastProps, 'type'>) => void;
  showErrorToast: (payload: Omit<SystemToastProps, 'type'>) => void;
}>({
  showMessageToast: () => {},
  showSuccessToast: () => {},
  showWarningToast: () => {},
  showErrorToast: () => {},
});

export const ToastsProvider: React.FC = ({ children }) => {
  const history = useHistory();

  const isAuthenticated = useSelector(getAuthenticated);

  const { isMobile } = useMedia();
  const { isBrowserTabActive, isBrowserTabActiveRef } = useInactiveBrowserTab();
  const messageToastsQueue = useRef<MessageToastContent[]>([]);

  const getLastMessageToastFromQueue = () => {
    return messageToastsQueue.current[messageToastsQueue.current.length - 1];
  };

  const showMessageToast = useCallback(
    (props: MessageToastProps) => {
      const content = getMessageToastContent(props.content);

      isBrowserTabActiveRef.current
        ? toast(content, {
            ...props?.libOptions,
          })
        : messageToastsQueue.current.push(props.content);
    },
    [isBrowserTabActiveRef]
  );

  const showSuccessToast = useCallback(
    (options: Omit<SystemToastProps, 'type'>) => {
      const content = getSystemToastContent({
        type: SystemToastTypes.Success,
        ...options,
      });

      toast(content);
    },
    []
  );

  const showWarningToast = useCallback(
    (options: Omit<SystemToastProps, 'type'>) => {
      const content = getSystemToastContent({
        type: SystemToastTypes.Warning,
        ...options,
      });

      toast(content);
    },
    []
  );

  const showErrorToast = useCallback(
    (options: Omit<SystemToastProps, 'type'>) => {
      const content = getSystemToastContent({
        type: SystemToastTypes.Error,
        ...options,
      });

      toast(content);
    },
    []
  );

  useEffect(() => {
    if (isBrowserTabActiveRef.current && messageToastsQueue.current.length) {
      const lastMsgOptions = getLastMessageToastFromQueue();

      const content = getMessageToastContent({
        photo: lastMsgOptions.photo,
        name: lastMsgOptions.name,
        message: `You have ${messageToastsQueue.current.length} new messages`,
        onClick: () => history.push('/threads'),
      });

      toast(content);
      messageToastsQueue.current = [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrowserTabActive]);

  useEffect(() => {
    return () => {
      if (isAuthenticated) {
        toast.clearWaitingQueue();
      }
    };
  }, [isAuthenticated]);

  const value = useMemo(() => {
    return {
      showMessageToast,
      showSuccessToast,
      showWarningToast,
      showErrorToast,
    };
  }, [showErrorToast, showMessageToast, showSuccessToast, showWarningToast]);

  return (
    <ToastsContext.Provider value={value}>
      {children}
      <ToastContainer
        autoClose={DEFAULT_TOAST_AUTOCLOSE_TIME}
        position={isMobile ? 'top-center' : 'top-right'}
        hideProgressBar
        closeOnClick
        limit={MAX_TOASTS_COUNT_AT_TIME}
        transition={Slide}
        className={styles.root}
      />
    </ToastsContext.Provider>
  );
};
